// 取り込みデータの正規化パイプライン（plan.md §5.5）。
//
// 役割: 外部ファイルから読み込んだ生テーブルを、本アプリの正規形に整える:
//   (a) CRS を EPSG:4326 に統一（軸順序の転倒を防ぐため ST_Transform 第4引数 true）
//   (b) ST_MakeValid で不正ジオメトリを修復（plan.md §5.4）
//   (c) ジオメトリ列名を "geom" に統一
//   (d) ジオメトリ型を判定して返す（呼び出し側が layer_registry へ登録する）
//
// 関係: loadFile.js が作った生テーブルを受け取り、db.js の query() で正規化 SQL を実行する。
//       import_data ツール・手動取込フローから呼ばれる。
//
// CRS が不明なときの扱い（plan.md §5.5 / §10）:
//   ファイルメタデータから CRS が取れない場合、4326 と決め打ちせず UnknownCrsError を投げる。
//   UI 側はこれを捕捉して確認モーダルを表示し、ユーザーが指定した CRS で再実行する。
//   CRS が不明なまま距離・面積処理を走らせない、が不変条件。
import { query } from './db'
import { findGeometryColumn } from './loadFile'

// CRS 不明を表す専用エラー。UI 側はこれを見て確認モーダルを出す。
export class UnknownCrsError extends Error {
  constructor(table) {
    super(`取り込みデータの CRS が不明です（テーブル: ${table}）。CRS の指定が必要です。`)
    this.name = 'UnknownCrsError'
    this.table = table
  }
}

// '4326' / 4326 / 'EPSG:4326' などを 'EPSG:4326' 形式へ正規化する。
// PROJ 文字列（'+proj=...'）はそのまま通す。
export function canonicalCrs(input) {
  if (input == null) return null
  const s = String(input).trim()
  if (!s) return null
  if (s.startsWith('+')) return s // PROJ4 文字列
  if (/^epsg:\d+$/i.test(s)) return `EPSG:${s.split(':')[1]}`
  if (/^\d+$/.test(s)) return `EPSG:${s}`
  return s
}

// ファイル名と明示指定から取り込み元 CRS を推定する。
// - 明示指定（declared）があれば最優先
// - GeoJSON は仕様上 EPSG:4326（経緯度）とみなしてよい
// - それ以外（.fgb / .parquet 等）はメタデータ依存のため、ここでは不明扱い（null）
//   → 呼び出し側で UnknownCrsError 経路に入りユーザー確認を促す
export function detectSourceCrs(fileName, declared) {
  const explicit = canonicalCrs(declared)
  if (explicit) return explicit
  const ext = String(fileName ?? '').split('.').pop().toLowerCase()
  if (ext === 'geojson' || ext === 'json') return 'EPSG:4326'
  return null
}

// 代表的なジオメトリ型（最頻値）を返す。例: 'POINT' / 'MULTIPOLYGON'
async function detectGeomType(table, geomColumn = 'geom') {
  const rows = await query(
    `SELECT ST_GeometryType("${geomColumn}") AS t, count(*) AS c ` +
      `FROM "${table}" WHERE "${geomColumn}" IS NOT NULL ` +
      `GROUP BY t ORDER BY c DESC;`,
  )
  return rows[0]?.t ?? null
}

// 生テーブルを正規化して上書きする。
//
// 引数: { table, geomColumn, sourceCrs }
//   sourceCrs: 'EPSG:4326' / PROJ 文字列 / null。null の場合は UnknownCrsError。
// 戻り値: { name, geomColumn: 'geom', featureCount, geomType }
export async function normalizeTable({ table, geomColumn, sourceCrs }) {
  const crs = canonicalCrs(sourceCrs)
  if (!crs) throw new UnknownCrsError(table)

  // 4326 以外は EPSG:4326 へ投影。EPSG:4326 を含む変換では軸順序転倒を防ぐため
  // 第4引数（always_xy）に true を必ず付ける（plan.md §5.2）。
  const isAlready4326 = /^EPSG:4326$/i.test(crs)
  const reprojected = isAlready4326
    ? `"${geomColumn}"`
    : `ST_Transform("${geomColumn}", '${crs}', 'EPSG:4326', true)`

  // ST_MakeValid を一段かませてから geom 列に統一する（plan.md §5.4）。
  await query(
    `CREATE OR REPLACE TABLE "${table}" AS ` +
      `SELECT * EXCLUDE ("${geomColumn}"), ST_MakeValid(${reprojected}) AS geom ` +
      `FROM "${table}";`,
  )

  // 正規化後にジオメトリ列が 'geom' で存在することを確認する。
  const geom = await findGeometryColumn(table)
  if (geom !== 'geom') {
    throw new Error(`正規化後のジオメトリ列が見つかりません（テーブル: ${table}）`)
  }

  const [{ cnt }] = await query(`SELECT count(*) AS cnt FROM "${table}";`)
  const geomType = await detectGeomType(table, 'geom')

  return { name: table, geomColumn: 'geom', featureCount: Number(cnt), geomType }
}
